/* eslint-disable no-underscore-dangle, no-param-reassign */
import L from 'leaflet';
import { hasIn, isNil } from 'lodash';

import leafletLegendGenerator from './leafletLegendGenerator';

const LINK_STYLE = `
  position: absolute;
  right: 0px;
  top: 0px;
  width: 34px;
  height: 34px;
  cursor: pointer;
`;

const SECTION_STYLE = `
  position: absolute;
  padding: 0;
  border: none;
  right: 17px;
  max-height: 0px;
  max-width: 0px;
  overflow: hidden;
  top: 34px;
  z-index: -1;
  transition: max-width 0.5s, max-height 0.5s;
`;

const LIST_STYLE = `
  max-height: 35vh;
  overflow-y: scroll;
`;

const LABEL_CONTAINER_STYLE = `
  position: relative;
  text-align: initial;
  `;

const HEADER_STYLE = `
  padding-left: 8px;
  background-color: #222;
  color: #fff;
  font-weight: bold;
`;

const DROPDOWN_CONTROL_STYLE = `
  position: absolute;
  right: 4px;
  top: 1px;
  height: 14px;
  width: 14px;
  z-index: 1;
  cursor: pointer;
`;

const DROPDOWN_LEGEND_STYLE = `
  position: absolute;
  text-align: center;
  display: inline;
  max-height: 0px;
  opacity: 0;
  transition: all 0.3s ease-in-out 1s, opacity 0.4s linear 0s;
  overflow: hidden;
`;

const FA_GLOBE = '<img src="/maps-buttons/mapa_base.svg" style="height:100%;width:100%;" />';

const FA_LAYERS = '<img src="/maps-buttons/capas.svg" style="height:100%;width:100%;" />';

const FA_CARET_RIGHT = '<i class="far fa-caret-square-right"></i>';

const FA_CARET_DOWN = '<i class="far fa-caret-square-down"></i>';

L.Control.CustomLayerControl = L.Control.Layers.extend({
  initialize(baseLayers, overlays, options) {
    L.Util.setOptions(this, options);

    this._layerControlInputs = [];
    this._layers = [];
    this._lastZIndex = 0;
    this._handlingClick = false;

    if (baseLayers) {
      Object.keys(baseLayers).map(i => this._addLayer(baseLayers[i], i));
    }
    if (overlays) {
      Object.keys(overlays).map(i => this._addLayer(overlays[i], i, true));
    }
  },
  onAdd(map) {
    this._initLayout();
    this._update();

    this._map = map;

    map.on('zoomend', this._checkDisabledLayers, this);
    map.on('click', this.collapse, this);
    map.on('mouseout', this.collapse, this);

    for (let i = 0; i < this._layers.length; i += 1) {
      if (!isNil(this._layers[i].layer)) {
        this._layers[i].layer.on('add remove', this._onLayerChange, this);
      }
    }

    this._layersLink.innerHTML = FA_GLOBE;

    this._customHeader.innerHTML = `Base Maps`;
    this._separator.innerHTML = `Layers`;
    return this._containerBig;
  },
  onRemove() {
    this._map.off('zoomend', this._checkDisabledLayers, this);

    for (let i = 0; i < this._layers.length; i += 1) {
      if (!isNil(this._layers[i].layer)) {
        this._layers[i].layer.off('add remove', this._onLayerChange, this);
      }
    }
  },
  _initLayout() {
    this._containerBig = L.DomUtil.create('div');
    const containerBig = this._containerBig;
    this._container = L.DomUtil.create('div');
    const container = this._container;
    const { collapsed } = this.options;

    containerBig.appendChild(container);
    container.setAttribute('aria-haspopup', true);

    L.DomEvent.disableClickPropagation(containerBig);
    L.DomEvent.disableScrollPropagation(containerBig);

    this._section = L.DomUtil.create('div', 'alert alert-light');
    const section = this._section;
    section.style.cssText = SECTION_STYLE;

    this._layersLink = L.DomUtil.create('div', '', container);
    const link = this._layersLink;
    link.style.cssText = LINK_STYLE;

    link.title = 'Base Maps';
    link.clicked = false;

    L.DomEvent.on(link, 'click', L.DomEvent.stop);
    L.DomEvent.on(link, 'click', this.expand, this);

    if (!collapsed) {
      this.expand();
    }

    this._customHeader = L.DomUtil.create('h6', 'alert-heading', section);
    this._baseLayersList = L.DomUtil.create('div', '', section);
    this._separator = L.DomUtil.create('div', '', section);
    this._overlaysList = L.DomUtil.create('div', '', section);

    this._customHeader.style.cssText = HEADER_STYLE;
    this._baseLayersList.style.cssText = LIST_STYLE;
    this._separator.style.cssText = HEADER_STYLE;
    this._overlaysList.style.cssText = LIST_STYLE;

    containerBig.appendChild(section);
  },
  _getLayer(id) {
    for (let i = 0; i < this._layers.length; i += 1) {
      if (!isNil(this._layers[i].layer)) {
        if (this._layers[i] && L.Util.stamp(this._layers[i].layer) === id) {
          return this._layers[i];
        }
      }
    }
    return '';
  },
  _addItem(obj) {
    const labelContainer = document.createElement('div');
    if (isNil(obj.layer)) return labelContainer;
    const label = document.createElement('label');
    const checked = this._map.hasLayer(obj.layer);
    const customSpan = document.createElement('customSpan');
    let input;

    if (obj.overlay) {
      input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'leaflet-control-layers-selector';
      input.defaultChecked = checked;
      customSpan.className = 'leaflet-control-spanOverlay';
    } else {
      input = this._createRadioElement(`leaflet-base-layers_${L.Util.stamp(this)}`, checked);
      customSpan.className = 'leaflet-control-span';
    }
    input.style.opacity = '0';
    labelContainer.style.cssText = LABEL_CONTAINER_STYLE;

    this._layerControlInputs.push(input);
    input.layerId = L.Util.stamp(obj.layer);

    L.DomEvent.on(input, 'click', this._onInputClick, this);

    const holder = document.createElement('div');
    holder.innerHTML = ` ${obj.name}`;
    holder.className = 'leaflet-control-div';

    if (hasIn(obj, ['layer', 'layerStyle'])) {
      const { layerStyle } = obj.layer;

      labelContainer.style.paddingRight = '60px';
      labelContainer.style.paddingLeft = '5px';

      const dropdownControl = L.DomUtil.create('div');
      dropdownControl.style.cssText = DROPDOWN_CONTROL_STYLE;
      dropdownControl.innerHTML = FA_CARET_RIGHT;
      dropdownControl.clicked = false;

      const dropdownLegend = L.DomUtil.create('div');
      dropdownLegend.style.cssText = DROPDOWN_LEGEND_STYLE;
      dropdownLegend.innerHTML = leafletLegendGenerator(layerStyle);

      L.DomEvent.on(dropdownControl, 'click', L.DomEvent.stop);
      L.DomEvent.on(dropdownControl, 'click', () => {
        if (dropdownControl.clicked) {
          dropdownControl.innerHTML = FA_CARET_RIGHT;
          this.legendCollapse(dropdownLegend);
          L.DomUtil.removeClass(dropdownControl, 'controlExpanded');
        } else {
          const legendsExpanded = document.getElementsByClassName('legendExpanded');
          const controlExpanded = document.getElementsByClassName('controlExpanded');
          while (legendsExpanded[0]) {
            this.legendCollapse(legendsExpanded[0]);
          }
          while (controlExpanded[0]) {
            controlExpanded[0].innerHTML = FA_CARET_RIGHT;
            controlExpanded[0].clicked = false;
            controlExpanded[0].classList.remove('controlExpanded');
          }
          dropdownControl.innerHTML = FA_CARET_DOWN;
          dropdownLegend.style.maxHeight = '500px';
          dropdownLegend.style.opacity = '1';
          dropdownLegend.style.position = 'static';
          dropdownLegend.style.transition = 'opacity 0.4s linear 0s';
          L.DomUtil.addClass(dropdownLegend, 'legendExpanded');
          L.DomUtil.addClass(dropdownControl, 'controlExpanded');
        }
        dropdownControl.clicked = !dropdownControl.clicked;
      });

      labelContainer.appendChild(dropdownControl);
      labelContainer.appendChild(label);
      labelContainer.appendChild(dropdownLegend);
    } else {
      labelContainer.appendChild(label);
    }

    label.appendChild(holder);
    holder.appendChild(input);
    holder.appendChild(customSpan);

    const container = obj.overlay ? this._overlaysList : this._baseLayersList;
    container.appendChild(labelContainer);

    this._checkDisabledLayers();
    return labelContainer;
  },
  _onInputClick() {
    const inputs = this._layerControlInputs;
    let input;
    let layer;
    const addedLayers = [];
    const removedLayers = [];

    this._handlingClick = true;

    const zoom = this._map.getZoom();
    for (let i = inputs.length - 1; i >= 0; i -= 1) {
      input = inputs[i];
      ({ layer } = this._getLayer(input.layerId));

      if (input.checked && zoom <= layer.maxZoom && zoom >= layer.minZoom) {
        addedLayers.push(layer);
      } else if (!input.checked) {
        removedLayers.push(layer);
      }

      layer.show = input.checked;
      if (input.type !== 'checkbox' && input.checked) {
        this._map.off('zoomend', this._map.activeBaseMapZoomEndEvent);
        this._map.activeBaseMapZoomEndEvent = this._map.zoomEndEvents[layer.ipsilumId];
        this._map.on('zoomend', this._map.activeBaseMapZoomEndEvent);
      }
    }

    // Bugfix issue 2318: Should remove all old layers before readding new ones
    for (let i = 0; i < removedLayers.length; i += 1) {
      if (this._map.hasLayer(removedLayers[i])) {
        this._map.removeLayer(removedLayers[i]);
      }
    }
    for (let i = 0; i < addedLayers.length; i += 1) {
      if (!this._map.hasLayer(addedLayers[i])) {
        this._map.addLayer(addedLayers[i]);
      }
    }

    this._handlingClick = false;

    this._refocusOnMap();
  },
  expand() {
    if (this._customHeader.innerHTML === `Base Maps`) {
      if (this._map.layerControl) this._map.layerControl.collapse();
    } else if (this._map.baseMapControl) this._map.baseMapControl.collapse();
    if (!this._layersLink.clicked) {
      this._layersLink.clicked = true;
      this._section.style.maxWidth = '500px';
      this._section.style.maxHeight = '500px';
      this._checkDisabledLayers();
    } else {
      this.collapse();
    }

    return this;
  },
  collapse() {
    const legendsExpanded = document.getElementsByClassName('legendExpanded');
    const controlExpanded = document.getElementsByClassName('controlExpanded');
    while (legendsExpanded[0]) {
      this.legendCollapse(legendsExpanded[0]);
    }
    while (controlExpanded[0]) {
      controlExpanded[0].innerHTML = FA_CARET_RIGHT;
      controlExpanded[0].clicked = false;
      controlExpanded[0].classList.remove('controlExpanded');
    }
    this._section.style.maxWidth = '0px';
    this._section.style.maxHeight = '0px';
    this._layersLink.clicked = false;
  },
  changeStyleBaseMaps() {
    this._layersLink.style.right = '50px';
    this._section.style.right = '67px';
  },
  changeStyleLayers() {
    this._layersLink.style.right = '0px';
    this._layersLink.style.top = '-10px';
    this._section.style.right = '17px';
    this._section.style.top = '24px';
  },
  changeStyleOnlyBaseMaps() {
    this._map.baseMapControl = this;
  },
  changeStyleOnlyLayers() {
    this._map.layerControl = this;
    this._layersLink.innerHTML = FA_LAYERS;
    this._layersLink.title = 'Layers';
    this._customHeader.innerHTML = `Layers`;
  },
  legendCollapse(legend) {
    legend.style.maxHeight = '0px';
    legend.style.opacity = '0';
    legend.style.transition = 'opacity 0s linear';
    legend.style.position = 'absolute';
    legend.classList.remove('legendExpanded');
  }
});

L.control.customlayercontrol = (baseLayers, overlays, opts) =>
  new L.Control.CustomLayerControl(baseLayers, overlays, opts);
